import {Children, Component, createRef, isValidElement, PropsWithChildren, ReactElement, RefObject} from 'react';
import classNames from 'classnames';
import styles from './styles/CleverSlider.module.scss';
import PropTypes from 'prop-types';
import {observer} from 'mobx-react';
import CleverSlideContainer from "./CleverSlideContainer";
import SwipeEvents from "../../blocks/TerracedGridTheme/SwipeEvents";
import CleverSliderItem from "./CleverSliderItem";
import {StoreContext} from "../../../stores/StoreContext";
import {AppContextType} from "../../../stores/StoreLoader";

type CleverSliderProps = PropsWithChildren<{
    totalSlides: number;
    handleNext?: () => void;
    padding?: number;
    height: number | string;
    sliderClassName?: string;
    containerClassName?: string;
    childSlideClassName?: string;
    trackClassName?: string;
    scrollArrowClassName?: string;
    blockObj?: ILinksTerrace;
}>

interface CleverSliderState {
    currentIndex: number;
    slides: ReactElement[];
    slideRefs: RefObject<HTMLDivElement>[];
}

@observer
class CleverSlider extends Component<CleverSliderProps, CleverSliderState> {

    static propTypes = {
        totalSlides: PropTypes.number.isRequired,
        handleNext: PropTypes.func,
        padding: PropTypes.number,
        height: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
        sliderClassName: PropTypes.string,
        containerClassName: PropTypes.string,
        childSlideClassName: PropTypes.string,
        trackClassName: PropTypes.string,
        scrollArrowClassName: PropTypes.string,
        blockObj: PropTypes.object,
    };

    static defaultProps = {
        padding: 10,
        height: 400,
    };
    static contextType = StoreContext;
    // @ts-ignore
    public context: AppContextType;

    constructor(props: CleverSliderProps) {
        super(props);

        this.state = {
            currentIndex: 0,
            slides: [],
            slideRefs: [],
        };
        this.swipeEvents = new SwipeEvents(this.handleNext, this.handlePrev)
        this.trackRef = createRef();
    }

    private swipeEvents: SwipeEvents;
    private trackRef: RefObject<HTMLDivElement | null>;

    goTo = i => {
        if (isValidElement(this.state.slides[i])) {
            this.setState({
                currentIndex: i,
            })
        }
    };

    getMore = () => {
        if (this.props.handleNext) {
            this.props.handleNext();
        }
    };

    handleNext = () => {
        const {interfaceStore} = this.context as AppContextType;

        if (this.state.currentIndex !== this.state.slides.length - 1) {
            if (this.trackRef && this.trackRef.current) {
                const trackWidth = (this.trackRef.current.firstChild as HTMLElement)?.clientWidth;
                const trackTransformStyle = (this.trackRef.current.firstChild as HTMLElement)?.style.transform;

                let trackTranslate = 0;

                if (trackTransformStyle) {
                    const matchResult = trackTransformStyle.match(/\d+/);
                    if (matchResult && matchResult.length > 0) {
                        trackTranslate = parseInt(matchResult[0], 10);
                    }
                }

                const trackRight = trackWidth - trackTranslate;
                if (trackRight > 0 && trackRight < interfaceStore.dimensions.width * 2) {
                    this.getMore();
                }
            }

            this.setState(prevState => ({
                currentIndex: prevState.currentIndex + 1,
            }));
        }
    };

    handlePrev = () => {
        if (this.state.currentIndex !== 0) {
            this.setState(prevState => {
                return {
                    currentIndex: prevState.currentIndex - 1,
                }
            })
        }
    };

    resetIndex = () => {
        this.setState({
            currentIndex: 0,
        });
    }

    static getDerivedStateFromProps(props: CleverSliderProps, state) {
        const childSlideContainerClassName = classNames({
            [styles.childSlideContainer]: true,
            [props.childSlideClassName as string]: props.childSlideClassName,
        });

        const slideRefs: RefObject<HTMLDivElement | null>[] = [];
        // using this rather than cloneElement because you can't pass a className with cloneElement
        const slides = Children.toArray(props.children).filter(child => {
            return isValidElement(child);
        }).map((child, index) => {
            const slideRef = createRef<HTMLDivElement | null>();
            slideRefs.push(slideRef);
            return (
                <CleverSliderItem
                    key={(child as ReactElement<any>).props.id || index}
                    className={childSlideContainerClassName}
                    style={{marginRight: props.padding}}
                    ref={slideRef}
                    blockObj={props.blockObj}
                >
                    {child}
                </CleverSliderItem>
            )
        });

        return {
            slides,
            slideRefs,
        }
    }

    render() {
        const containerClassName = classNames({
            [styles.container]: true,
            [this.props.containerClassName as string]: this.props.containerClassName,
        });

        return (
            <div className={containerClassName}>
                <CleverSlideContainer
                    className={this.props.sliderClassName}
                    trackClass={this.props.trackClassName}
                    currentIndex={this.state.currentIndex}
                    ref={this.trackRef}
                    padding={this.props.padding}
                    getMore={this.getMore}
                    style={{height: this.props.height}}
                    slideMap={this.state.slideRefs}
                    events={this.swipeEvents.events}
                    scrollArrowClassName={this.props.scrollArrowClassName}
                    handlePrev={this.handlePrev}
                    handleNext={this.handleNext}
                >
                    {this.state.slides}
                </CleverSlideContainer>
            </div>
        )
    }
}

export default CleverSlider
